import AddRounded from '@mui/icons-material/AddRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';

import { useUpdatePiecesMutation } from '@api/piece';
import { useCreateUserAddressMutation, useGetUserByIdQuery } from '@api/user';
import { PieceAction } from '@apiContract';
import { AddressId } from '@components/FormAddressFields/types';
import differenceWith from 'lodash/differenceWith';
import { toast } from 'sonner';
import LabelSwitch from 'src/components/Inputs/LabelSwitch';
import Loader from 'src/components/Loader';
import { countryCodeToString, FormUserAddress, FormUserAddressState } from 'src/forms/FormUser/components/Addresses/FormUserAddress';

import { showApiErrorToast } from '@utils/apiErrorToast';

import { ActionComponentProps } from '../../../index';
import { CollapsibleOptions } from '../CollapsibleOptions';
import { buildUpdatePiecesRequest } from '../helper';
import { AddressItem } from './components/AddressItem';
import { sxNewAddressContainer } from './styles';
import { useMe } from 'src/hooks/useMe';

export const RequestForward = ({ mailpieces, actionStarted, onActionFinish, isForce }: ActionComponentProps) => {
  const [updatePieces, { isLoading: isUpdatingMailpiece }] = useUpdatePiecesMutation();
  const [createUserAddress, { isLoading: isCreatingAddress }] = useCreateUserAddressMutation();
  const isSetDefault = useRef(false);
  const recipientId = mailpieces[0].recipient?.userId || 0;
  const me = useMe();

  const { data: recipient, isLoading: isLoadingUser } = useGetUserByIdQuery(recipientId, { skip: !isForce || !recipientId });

  const addresses = isForce ? recipient?.addresses : me.currentUser?.addresses;

  const [selectedAddressId, setSelectedAddressId] = useState<AddressId | null>(null);
  const [openCreateForm, setOpenCreateForm] = useState(false);
  const [shouldNotifyMailroom, setShouldNotifyMailroom] = useState(false);

  if (!selectedAddressId && addresses && addresses.length && !isSetDefault.current) {
    const defaultAddress = addresses?.find((address) => address.default);
    if (defaultAddress) {
      setSelectedAddressId(defaultAddress.id);
    }
    isSetDefault.current = true;
  }

  const pieceQuantity = mailpieces.length;

  const isLoading = isCreatingAddress || isUpdatingMailpiece;

  const handleToggleCreateForm = () => {
    if (openCreateForm) {
      const defaultAddress = addresses?.find((address) => address.default);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
      }
    } else {
      setSelectedAddressId(null);
    }
    setOpenCreateForm((value) => !value);
  };

  const handleSelectAddress = (addressId: AddressId) => {
    setSelectedAddressId(addressId);
    setOpenCreateForm(false);
  };

  const onSubmitSelectedAddress = async (addressId: number | null) => {
    if (!addressId) return;

    try {
      const requestData = buildUpdatePiecesRequest({
        mailpieces,
        action: PieceAction.RequestForward,
        isForce,
        shouldNotifyMailroom,
        additionalData: {
          addressId,
        },
      });

      await updatePieces(requestData).unwrap();
      onActionFinish();
    } catch (error) {
      showApiErrorToast(error);
    }
  };

  const onSubmit = async () => {
    if (!selectedAddressId) {
      toast.error('Please, select an address', { duration: 3000 });
      return null;
    }
    await onSubmitSelectedAddress(selectedAddressId);
  };

  const createUserAdress = async (form: FormUserAddressState) => {
    const userId = isForce ? recipientId : me.currentUser?.id;
    if (!userId) return null;

    try {
      const request = {
        ...countryCodeToString(form),
        userId,
      };

      const newAddressList = await createUserAddress(request).unwrap();
      const createdAddress = differenceWith(newAddressList, addresses || [], (a, b) => a.id === b.id)[0];
      return createdAddress || null;
    } catch (error) {
      showApiErrorToast(error);
      return null;
    }
  };

  const onSaveAdress = async (form: FormUserAddressState) => {
    try {
      const newAdress = await createUserAdress(form);
      const newAdressId = newAdress?.id;
      if (newAdressId) {
        setSelectedAddressId(newAdressId);
        setOpenCreateForm(false);
      } else {
        throw new Error('Address not created');
      }
    } catch (error) {
      showApiErrorToast(error);
    }
  };

  return (
    <Dialog maxWidth="sm" sx={isLoadingUser ? { minWidth: '400px' } : {}} open={actionStarted} onClose={onActionFinish}>
      <DialogTitle sx={{ py: 2.25, px: 3 }}>
        {pieceQuantity > 1 ? 'Request forward for selected items' : 'Request forward'}
      </DialogTitle>
      <Loader isLoading={isLoadingUser}>
        <DialogContent>
          <Stack direction="column" spacing={3}>
            <Typography sx={{ color: 'text.secondary' }} variant="body1">
              {pieceQuantity > 1 ? (
                <>
                  Select the address you would like <strong>{pieceQuantity}</strong> items forwarded to.
                </>
              ) : (
                'Select the address you would like this item forwarded to.'
              )}
            </Typography>
            {(addresses || []).map((address) => (
              <AddressItem
                key={address.id}
                address={address}
                isSelected={selectedAddressId === address.id}
                onSelect={handleSelectAddress}
              />
            ))}
            {!(addresses || []).length && (
              <Typography variant="body2" sx={{ textAlign: 'center', py: 2 }}>
                You don't have addresses yet.
              </Typography>
            )}
            <Box sx={{ display: 'flex' }}>
              <Button
                size="small"
                onClick={handleToggleCreateForm}
                sx={{ color: 'primary.main' }}
                startIcon={<AddRounded />}
              >
                Add New Address
              </Button>
            </Box>
            <Collapse in={openCreateForm}>
              <Paper variant="outlined" sx={sxNewAddressContainer(openCreateForm)}>
                <FormControlLabel
                  control={<Radio checked={true} checkedIcon={<CheckCircleRounded />} />}
                  label={<Typography variant="subtitle1">Add New Address</Typography>}
                  disableTypography
                />
                <FormUserAddress
                  onCancel={handleToggleCreateForm}
                  onSubmit={onSaveAdress}
                  lockDefaultOnTrue={!addresses?.length}
                />
              </Paper>
            </Collapse>
          </Stack>
          {isForce && (
            <CollapsibleOptions disableTopMargin>
              <Box sx={{ pt: 1 }}>
                <LabelSwitch
                  checked={shouldNotifyMailroom}
                  onChange={() => setShouldNotifyMailroom(!shouldNotifyMailroom)}
                  label="Notify mailroom users about pending request"
                />
              </Box>
            </CollapsibleOptions>
          )}
        </DialogContent>
      </Loader>
      <DialogActions>
        <Button onClick={onActionFinish} color="inherit" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          loading={isLoading}
          loadingPosition="start"
          variant="contained"
          color="primary"
          disabled={!selectedAddressId}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
